import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InviteTeamMember } from '../../redux/teamSlice';
import SetupAccessGroups from './SetupAccessGroups';
import SetupAddMembers from './SetupAddMembers';
import SetupNda from './SetupNda';
import SetupReviewTeam from './SetupReviewTeam';
import SetupTeamComplete from './SetupTeamComplete';
import { isAdminGroup } from './accessGroupCatalog';
import { emptyNdaDraft, NDA_MODE } from './ndaDraft';

const SetupTeamFlow = ({ project, workstreams = [], onBackToReady, onEnterProject }) => {
    const dispatch = useDispatch();
    const user = useSelector(state => state?.user?.user);
    const selectedProject = useSelector(state => state?.projectData?.selectedProject);
    const apiGroups = useSelector(state => state?.team?.groups);

    const [teamStep, setTeamStep] = React.useState(1);
    const [openCreate, setOpenCreate] = React.useState(false);
    const [extras, setExtras] = React.useState({});
    const [localGroups, setLocalGroups] = React.useState([]);
    const [invitesByGroup, setInvitesByGroup] = React.useState({});
    const [adminEmails, setAdminEmails] = React.useState([]);
    const [sentInvites, setSentInvites] = React.useState([]);
    const [sending, setSending] = React.useState(false);
    const [teamWorkstreams, setTeamWorkstreams] = React.useState(workstreams || []);
    const [ndaDraft, setNdaDraft] = React.useState(emptyNdaDraft);

    const projectId = project?.id || selectedProject?.id;
    const vdrId = project?.vdr?.id || selectedProject?.vdr?.id;
    const canPersist = Boolean(projectId && vdrId);
    const groups = React.useMemo(() => {
        const merged = canPersist ? [...(apiGroups || []), ...(localGroups || [])] : localGroups || [];
        const seen = new Set();
        return merged.filter(group => {
            if (!group?.id || isAdminGroup(group) || seen.has(String(group.id))) return false;
            seen.add(String(group.id));
            return true;
        });
    }, [apiGroups, canPersist, localGroups]);
    const adminGroup = ((apiGroups || []).find(isAdminGroup) || null);
    const groupById = id =>
        [...(apiGroups || []), ...(localGroups || [])].find(group => String(group.id) === String(id)) || null;

    const handleFinish = async () => {
        if (sending) return;
        setSending(true);
        const sent = [];
        const seenEmails = new Set();

        const pushInvite = (email, groupName) => {
            const key = `${email}::${groupName}`;
            if (seenEmails.has(key)) return;
            seenEmails.add(key);
            sent.push({ email, groupName });
        };

        groups.forEach(group => {
            const emails = invitesByGroup[group.id] || invitesByGroup[String(group.id)] || [];
            emails.forEach(email => pushInvite(email, group.name));
        });

        Object.entries(invitesByGroup).forEach(([id, emails]) => {
            if (groups.some(group => String(group.id) === String(id))) return;
            const group = groupById(id);
            const groupName = isAdminGroup(group) ? 'Project Administrators' : group?.name || 'Access group';
            (emails || []).forEach(email => pushInvite(email, groupName));
        });

        adminEmails.forEach(email => pushInvite(email, 'Project Administrators'));

        if (canPersist) {
            for (const group of groups) {
                const emails = invitesByGroup[group.id] || [];
                if (!emails.length) continue;
                await dispatch(
                    InviteTeamMember({
                        project: projectId,
                        group_type: group.id,
                        emails,
                        vdr: vdrId,
                        nda: ndaDraft.mode === NDA_MODE.NONE ? null : ndaDraft.ndaId,
                    }),
                );
            }
            if (adminEmails.length && adminGroup?.id) {
                await dispatch(
                    InviteTeamMember({
                        project: projectId,
                        group_type: adminGroup.id,
                        emails: adminEmails,
                        vdr: vdrId,
                        nda: ndaDraft.mode === NDA_MODE.NONE ? null : ndaDraft.ndaId,
                    }),
                );
            }
        }

        setSentInvites(sent);
        setSending(false);
        setTeamStep(5);
    };

    if (teamStep === 1) {
        return (
            <SetupAccessGroups
                project={project}
                workstreams={teamWorkstreams}
                extras={extras}
                onExtrasChange={setExtras}
                localGroups={localGroups}
                onLocalGroupsChange={setLocalGroups}
                startCreate={openCreate}
                onWorkstreamsChange={setTeamWorkstreams}
                onContinue={() => {
                    setOpenCreate(false);
                    setTeamStep(2);
                }}
                onBack={onBackToReady}
            />
        );
    }

    if (teamStep === 2) {
        return (
            <SetupAddMembers
                user={user}
                groups={groups}
                extras={extras}
                workstreams={teamWorkstreams}
                invitesByGroup={invitesByGroup}
                onInvitesChange={setInvitesByGroup}
                adminEmails={adminEmails}
                onAdminEmailsChange={setAdminEmails}
                onReview={() => setTeamStep(3)}
                onBack={() => setTeamStep(1)}
                onSkip={onEnterProject}
                onAddGroup={() => {
                    setOpenCreate(true);
                    setTeamStep(1);
                }}
            />
        );
    }

    if (teamStep === 3) {
        return (
            <SetupNda
                projectId={projectId}
                draft={ndaDraft}
                onDraftChange={setNdaDraft}
                onContinue={next => {
                    setNdaDraft(next);
                    setTeamStep(4);
                }}
                onBack={() => setTeamStep(2)}
            />
        );
    }

    if (teamStep === 4) {
        return (
            <SetupReviewTeam
                user={user}
                groups={groups}
                extras={extras}
                workstreams={teamWorkstreams}
                invitesByGroup={invitesByGroup}
                adminEmails={adminEmails}
                ndaDraft={ndaDraft}
                sending={sending}
                onFinish={handleFinish}
                onBack={() => setTeamStep(3)}
                onEdit={() => setTeamStep(2)}
                onEditNda={() => setTeamStep(3)}
            />
        );
    }

    return (
        <SetupTeamComplete
            inviteCount={sentInvites.length}
            groupCount={groups.length}
            sentInvites={sentInvites}
            ndaRequired={ndaDraft.mode !== NDA_MODE.NONE}
            onEnter={onEnterProject}
        />
    );
};

export default SetupTeamFlow;
